import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save, Loader2, AlertCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { campaignAPI } from '../../../api';
import { handleApiError } from '../../../utils/errorHandler';

function CampaignFormModal({ campaign, onClose, onSuccess }) {
  const { t } = useTranslation();
  const isEdit = Boolean(campaign);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    campaign_type: 'information',
    message_title: '',
    message_body: '',
    message_priority: 'normal',
    schedule_type: 'immediate',
    scheduled_at: '',
    recurrence_rule: '',
    requires_response: false,
    response_type: '',
    target_criteria: {
      driver_status: 'active'
    },
    ...campaign
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (campaign) {
      setFormData({
        ...formData,
        ...campaign,
        target_criteria: typeof campaign.target_criteria === 'string'
          ? JSON.parse(campaign.target_criteria)
          : campaign.target_criteria || {}
      });
    }
  }, [campaign]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = t('campaigns.form.errors.nameRequired');
    if (!formData.message_title?.trim()) newErrors.message_title = t('campaigns.form.errors.titleRequired');
    if (!formData.message_body?.trim()) newErrors.message_body = t('campaigns.form.errors.bodyRequired');
    if (formData.schedule_type === 'scheduled' && !formData.scheduled_at) {
      newErrors.scheduled_at = t('campaigns.form.errors.dateRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createMutation = useMutation({
    mutationFn: (data) => campaignAPI.create(data),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      handleApiError(error, t('campaigns.form.errors.create'));
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => campaignAPI.update(id, data),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      handleApiError(error, t('campaigns.form.errors.update'));
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      ...formData,
      tenant_id: parseInt(localStorage.getItem('tenantId')) || 1,
      created_by: parseInt(localStorage.getItem('userId')) || 1,
      target_criteria: typeof formData.target_criteria === 'string'
        ? formData.target_criteria
        : JSON.stringify(formData.target_criteria),
      requires_response: formData.requires_response ? 1 : 0
    };

    if (isEdit) {
      updateMutation.mutate({ id: campaign.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit ? t('campaigns.form.title.edit') : t('campaigns.newCampaign')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">{t('campaigns.form.sections.general')}</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('campaigns.form.fields.name')}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full px-3 py-2 border rounded-md ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Ex: Rappel permis expirés"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('campaigns.form.fields.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Description optionnelle"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('campaigns.form.fields.type')}
                </label>
                <select
                  value={formData.campaign_type}
                  onChange={(e) => setFormData({ ...formData, campaign_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="information">{t('campaigns.type.information')}</option>
                  <option value="survey">{t('campaigns.type.survey')}</option>
                  <option value="safety_reminder">{t('campaigns.type.safety_reminder')}</option>
                  <option value="document_request">{t('campaigns.type.document_request')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('campaigns.form.fields.priority')}
                </label>
                <select
                  value={formData.message_priority}
                  onChange={(e) => setFormData({ ...formData, message_priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="low">{t('campaigns.form.priority.low')}</option>
                  <option value="normal">{t('campaigns.form.priority.normal')}</option>
                  <option value="high">{t('campaigns.form.priority.high')}</option>
                  <option value="urgent">{t('campaigns.form.priority.urgent')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-4 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900">{t('campaigns.form.sections.message')}</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('campaigns.form.fields.messageTitle')}
              </label>
              <input
                type="text"
                value={formData.message_title}
                onChange={(e) => setFormData({ ...formData, message_title: e.target.value })}
                className={`w-full px-3 py-2 border rounded-md ${errors.message_title ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Ex: Votre permis expire bientôt"
              />
              {errors.message_title && (
                <p className="mt-1 text-sm text-red-600">{errors.message_title}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('campaigns.form.fields.messageBody')}
              </label>
              <textarea
                value={formData.message_body}
                onChange={(e) => setFormData({ ...formData, message_body: e.target.value })}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md ${errors.message_body ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Merci de renouveler votre permis avant la date d'expiration..."
              />
              {errors.message_body && (
                <p className="mt-1 text-sm text-red-600">{errors.message_body}</p>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div className="space-y-4 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900">{t('campaigns.form.sections.schedule')}</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('campaigns.form.fields.scheduleType')}
              </label>
              <select
                value={formData.schedule_type}
                onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="immediate">{t('campaigns.form.scheduleOptions.immediate')}</option>
                <option value="scheduled">{t('campaigns.form.scheduleOptions.scheduled')}</option>
                <option value="recurring">{t('campaigns.form.scheduleOptions.recurring')}</option>
              </select>
            </div>

            {formData.schedule_type !== 'immediate' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('campaigns.form.fields.scheduledAt')}
                </label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md ${errors.scheduled_at ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.scheduled_at && (
                  <p className="mt-1 text-sm text-red-600">{errors.scheduled_at}</p>
                )}
              </div>
            )}

            {formData.schedule_type === 'recurring' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('campaigns.form.fields.frequency')}
                </label>
                <select
                  value={formData.recurrence_rule}
                  onChange={(e) => setFormData({ ...formData, recurrence_rule: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">{t('common.actions.select')}</option>
                  <option value="daily">{t('campaigns.form.recurrence.daily')}</option>
                  <option value="weekly">{t('campaigns.form.recurrence.weekly')}</option>
                  <option value="monthly">{t('campaigns.form.recurrence.monthly')}</option>
                </select>
              </div>
            )}
          </div>

          {/* Response Settings */}
          <div className="space-y-4 border-t border-gray-200 pt-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="requires_response"
                checked={formData.requires_response}
                onChange={(e) => setFormData({ ...formData, requires_response: e.target.checked })}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label htmlFor="requires_response" className="ml-2 block text-sm text-gray-900">
                {t('campaigns.form.fields.requiresResponse')}
              </label>
            </div>

            {formData.requires_response && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('campaigns.form.fields.responseType')}
                </label>
                <select
                  value={formData.response_type}
                  onChange={(e) => setFormData({ ...formData, response_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">{t('common.actions.select')}</option>
                  <option value="yes_no">{t('campaigns.form.responseType.yesNo')}</option>
                  <option value="text">{t('campaigns.form.responseType.text')}</option>
                  <option value="choice">{t('campaigns.form.responseType.choice')}</option>
                  <option value="document_upload">{t('campaigns.form.responseType.documentUpload')}</option>
                </select>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end space-x-3 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.states.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEdit ? t('common.actions.save') : t('common.actions.create')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CampaignFormModal;
